import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Cpu, Home as HomeIcon, Factory, Sparkles, History } from "lucide-react";
import { usePageTitle } from "@/hooks/usePageTitle";
import HexagonField from "@/components/HexagonField";
import api from "@/lib/api";
import { HOME } from "@/constants/testIds";
import ProductImage from "@/components/ProductImage";
import { useRecentlyViewed } from "@/hooks/useRecentlyViewed";

const HERO_IMG = "https://images.pexels.com/photos/3612932/pexels-photo-3612932.jpeg";
const OEM_IMG = "https://images.pexels.com/photos/34718925/pexels-photo-34718925.jpeg";
const HOME_IMG = "https://images.pexels.com/photos/22307516/pexels-photo-22307516.jpeg";
const IND_IMG = "https://images.pexels.com/photos/36564992/pexels-photo-36564992.jpeg";

const PillarCard = ({ to, eyebrow, title, copy, img, accent, testid }) => (
  <Link to={to} data-testid={testid}
        className="group relative overflow-hidden rounded-3xl glass p-7 md:p-9 min-h-[320px] flex flex-col justify-between hover:border-white/25 transition-all">
    <div className="absolute inset-0 opacity-30 group-hover:opacity-50 transition-opacity"
         style={{ backgroundImage: `linear-gradient(0deg, #0A0B10 30%, rgba(10,11,16,0.2) 100%), url(${img})`, backgroundSize: "cover", backgroundPosition: "center" }} />
    <div className="relative">
      <div className="text-[10px] uppercase tracking-widest text-white/50 font-mono">{eyebrow}</div>
      <h3 className="font-display text-2xl md:text-3xl mt-3 leading-tight max-w-sm">{title}</h3>
    </div>
    <div className="relative flex items-end justify-between">
      <p className="text-sm text-white/70 max-w-sm leading-relaxed">{copy}</p>
      <div className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center group-hover:bg-accent group-hover:border-transparent transition-all">
        <ArrowRight className="w-4 h-4" />
      </div>
    </div>
    <span className={`absolute top-0 left-0 h-1 ${accent}`} />
  </Link>
);

const CLIENTS = [
  "Oberoi Hotels & Resorts",
  "Somaiya Ayurvihar",
  "Ajanta Pharma",
  "ACI Hospitals",
  "Upspaces",
  "WeStudio",
];

const PROOF_POINTS = [
  "6 engineering domains in-house",
  "40+ smart-home products shipped",
  "Idea to working prototype in weeks",
  "Design, build, and deploy under one roof",
];

export default function HomePage() {
  usePageTitle(null);
  const [featured, setFeatured] = useState([]);
  const [settings, setSettings] = useState(null);
  const recentlyViewed = useRecentlyViewed();

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then(({ data }) => setFeatured(data.slice(0, 8)));
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  // Strip any leading "from " so the surrounding copy ("...from <X>") doesn't double up.
  const oemPrice = (settings?.oem_hero_price || "₹25,000").replace(/^from\s+/i, "");

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 opacity-40"
             style={{ backgroundImage: `linear-gradient(0deg, #0A0B10 30%, rgba(10,11,16,0.4) 100%), url(${HERO_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <HexagonField />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-32 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mb-7 rise">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-xs uppercase tracking-widest text-white/70 font-mono">An umbrella for what India builds next</span>
            </div>
            <h1 data-testid={HOME.heroHeadline}
                className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tighter rise rise-delay-1">
              From your idea<br />
              to a <span className="gradient-text">shippable</span> product.
            </h1>
            <p className="mt-8 text-lg text-white/70 max-w-xl leading-relaxed rise rise-delay-2">
              We design the electronics, write the software, and automate the spaces businesses run in — one partner, from first sketch to shipped product, engineered in India.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4 rise rise-delay-3">
              <Link to="/contact" data-testid={HOME.heroCtaQuote}
                    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full gradient-bg font-medium hover:shadow-[0_0_30px_rgba(157,76,221,0.5)] transition-shadow">
                Start your project <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/marketplace" data-testid={HOME.heroCtaMarketplace}
                    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/15 hover:bg-white/5 transition-colors">
                Explore the marketplace
              </Link>
              {settings?.oem_hero_visible && (
                <div className="text-xs text-white/50 font-mono uppercase tracking-widest">
                  <span className="text-white/80">{settings.oem_hero_price}</span> — {settings.oem_hero_teaser}
                </div>
              )}
            </div>
          </div>
          <div className="lg:col-span-4 hidden lg:block rise rise-delay-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="glass rounded-2xl p-5">
                <Cpu className="w-5 h-5 text-accent" />
                <div className="mt-3 font-display text-lg">Embedded + RF + PCB</div>
                <p className="text-xs text-white/50 mt-1">In-house electronics, schematic to design-for-manufacture.</p>
              </div>
              <div className="glass rounded-2xl p-5">
                <Sparkles className="w-5 h-5 text-accent" />
                <div className="mt-3 font-display text-lg">ML / AI</div>
                <p className="text-xs text-white/50 mt-1">Edge and cloud intelligence, built into your product.</p>
              </div>
              <div className="glass rounded-2xl p-5">
                <HomeIcon className="w-5 h-5 text-accent" />
                <div className="mt-3 font-display text-lg">Smart Home</div>
                <p className="text-xs text-white/50 mt-1">Luxury automation for homes and offices.</p>
              </div>
              <div className="glass rounded-2xl p-5">
                <Factory className="w-5 h-5 text-accent" />
                <div className="mt-3 font-display text-lg">Industrial</div>
                <p className="text-xs text-white/50 mt-1">Automation for buildings, energy, access, and security.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="relative py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
            <div className="max-w-2xl">
              <div className="text-[11px] uppercase tracking-widest text-white/40 font-mono">Prime offerings</div>
              <h2 className="font-display text-4xl lg:text-5xl mt-3 tracking-tighter">
                Three ways to <span className="text-accent">build with us</span>.
              </h2>
            </div>
            <p className="text-sm text-white/60 max-w-md">
              OEM/ODM is the heart of the company. Home and Industrial automation come from the same engineering bench. Everything else feeds off this core.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            <PillarCard to="/oem-odm" testid={HOME.pillarOem}
              eyebrow="Prime · OEM / ODM"
              title="Turn your idea into a manufacturable product."
              copy={`Industrial design, PCB, embedded firmware, RF, mechanical, ML — under one roof. Prototype-to-production from ${oemPrice}.`}
              img={OEM_IMG} accent="bg-gradient-to-r from-[#9D4CDD] to-[#FF3366]" />
            <PillarCard to="/home-automation" testid={HOME.pillarHome}
              eyebrow="Prime · Smart Home"
              title="Luxury with a touch of Magic."
              copy="Lighting, scenes, curtains, locks, climate, cameras. A WeAutomate home feels considered the moment you walk in."
              img={HOME_IMG} accent="bg-gradient-to-r from-[#FF3366] to-[#FF9900]" />
            <PillarCard to="/industrial-automation" testid={HOME.pillarIndustrial}
              eyebrow="Prime · Industrial"
              title="Plants that quietly compound margin."
              copy="BMS, energy intelligence, access control, HVAC, surveillance. Hardware-agnostic. Vendor-lock-free."
              img={IND_IMG} accent="bg-gradient-to-r from-[#FF9900] to-[#9D4CDD]" />
          </div>
        </div>
      </section>

      {/* MARKETPLACE PREVIEW */}
      <section className="relative py-24 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-white/40 font-mono">Marketplace · WeAutomate-branded</div>
              <h2 className="font-display text-4xl lg:text-5xl mt-3 tracking-tighter">
                The Smart Home <span className="text-accent">product line</span>.
              </h2>
            </div>
            <Link to="/marketplace" className="text-sm inline-flex items-center gap-2 text-white/70 hover:text-white">
              See the full catalog <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div data-testid={HOME.marketplacePreview}
               className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {featured.map((p) => (
              <Link to={`/product/${p.slug}`} key={p.id}
                    className="group glass rounded-2xl overflow-hidden hover:border-white/25 transition-colors">
                <div className="aspect-[4/3] overflow-hidden bg-white/5">
                  <ProductImage src={p.images?.[0]} alt={p.name}
                                className="group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-4">
                  <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono">{p.category}</div>
                  <div className="mt-1.5 text-sm font-medium leading-tight">{p.name}</div>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="font-display text-base text-accent">₹{p.price.toLocaleString("en-IN")}</div>
                    <ArrowRight className="w-4 h-4 opacity-40 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* RECENTLY VIEWED */}
      {recentlyViewed.length > 0 && (
        <section className="relative py-16 border-t border-white/5">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-white/40 font-mono flex items-center gap-2">
                  <History className="w-3.5 h-3.5 text-accent" /> Recently viewed
                </div>
                <h2 className="font-display text-3xl lg:text-4xl mt-3 tracking-tighter">Pick up where you left off.</h2>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {recentlyViewed.slice(0, 4).map((r) => (
                <Link to={`/product/${r.slug}`} key={r.slug}
                      className="group glass rounded-2xl overflow-hidden hover:border-white/25 transition-colors">
                  <div className="aspect-[4/3] overflow-hidden bg-white/5">
                    <ProductImage src={r.image} alt={r.name}
                                  className="group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-4">
                    <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono">{r.category}</div>
                    <div className="mt-1.5 text-sm font-medium leading-tight">{r.name}</div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="font-display text-base text-accent">₹{(r.price || 0).toLocaleString("en-IN")}</div>
                      <ArrowRight className="w-4 h-4 opacity-40 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SOCIAL PROOF */}
      <section className="relative py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="glass rounded-3xl p-10 lg:p-14 relative overflow-hidden noise">
            <div className="text-[11px] uppercase tracking-widest text-white/40 font-mono mb-4">Trusted by</div>
            <h3 className="font-display text-3xl lg:text-4xl tracking-tighter max-w-2xl">
              Built on our own businesses first.<br />Trusted by leading ones.
            </h3>

            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3">
              {CLIENTS.map((c) => (
                <span key={c} className="text-sm font-mono text-white/50 tracking-wide">{c}</span>
              ))}
            </div>

            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {PROOF_POINTS.map((p) => (
                <div key={p} className="flex items-start gap-3">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
                  <span className="text-sm text-white/70 leading-relaxed">{p}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
